import { supabase } from '../lib/supabase';

const VAT_RATE = 16; // Kenya VAT rate

// ─── Helpers ─────────────────────────────────────────────────────────────────
const toCamel = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamel);
  return Object.fromEntries(
    Object.entries(obj)?.map(([k, v]) => [
      k?.replace(/_([a-z])/g, (_, c) => c?.toUpperCase()),
      toCamel(v)
    ])
  );
};

const toSnake = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnake);
  return Object.fromEntries(
    Object.entries(obj)?.map(([k, v]) => [
      k?.replace(/([A-Z])/g, '_$1')?.toLowerCase(),
      v
    ])
  );
};

export const calculateVAT = (subtotal, vatRate = VAT_RATE) => {
  const sub = parseFloat(subtotal) || 0;
  const rate = parseFloat(vatRate) || VAT_RATE;
  const vatAmount = Math.round(sub * rate / 100 * 100) / 100;
  const total = Math.round((sub + vatAmount) * 100) / 100;
  return { subtotal: sub, vatRate: rate, vatAmount, total };
};

export const calculateLineItemAmount = (quantity, rate) => {
  return Math.round(parseFloat(quantity || 0) * parseFloat(rate || 0) * 100) / 100;
};

export const getInvoiceAgingCategory = (dueDate) => {
  if (!dueDate) return null;
  const today = new Date();
  const due = new Date(dueDate);
  const diffDays = Math.floor((today - due) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'current';
  if (diffDays <= 30) return '0-30';
  if (diffDays <= 60) return '31-60';
  return '60+';
};

// ─── Invoice Number Generation ────────────────────────────────────────────────
export const generateInvoiceNumber = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase?.rpc('generate_invoice_number', { p_lawyer_id: user?.id });
    if (error) throw error;
    return data;
  } catch (err) {
    const year = new Date()?.getFullYear();
    return `INV-${year}-${String(Math.floor(Math.random() * 9999) + 1)?.padStart(4, '0')}`;
  }
};

// ─── Invoices CRUD ────────────────────────────────────────────────────────────
export const getInvoices = async (filters = {}) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase?.from('invoices')?.select(`
        *,
        client:user_profiles!invoices_client_id_fkey(id, full_name, email, phone)
      `)?.eq('lawyer_id', user?.id)?.order('created_at', { ascending: false });

    if (filters?.status && filters?.status !== 'all') {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.dateFrom) query = query?.gte('issue_date', filters?.dateFrom);
    if (filters?.dateTo) query = query?.lte('issue_date', filters?.dateTo);
    if (filters?.search) {
      query = query?.or(`invoice_number.ilike.%${filters?.search}%,case_title.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query;
    if (error) throw error;

    const invoices = toCamel(data || []);

    // Compute aging and remaining balance
    return invoices?.map(inv => ({
      ...inv,
      remainingBalance: Math.max(0, (parseFloat(inv?.total) || 0) - (parseFloat(inv?.paidAmount) || 0)),
      agingCategory: ['sent', 'partially_paid', 'overdue']?.includes(inv?.status)
        ? getInvoiceAgingCategory(inv?.dueDate)
        : null
    }));
  } catch (err) {
    console.error('getInvoices error:', err?.message);
    throw err;
  }
};

export const getInvoiceById = async (invoiceId) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const [invoiceRes, lineItemsRes, paymentsRes, creditNotesRes] = await Promise.all([
      supabase?.from('invoices')?.select(`*, client:user_profiles!invoices_client_id_fkey(id, full_name, email, phone)`)?.eq('id', invoiceId)?.eq('lawyer_id', user?.id)?.single(),
      supabase?.from('invoice_line_items')?.select('*')?.eq('invoice_id', invoiceId)?.order('created_at', { ascending: true }),
      supabase?.from('payments')?.select('*')?.eq('invoice_id', invoiceId)?.order('payment_date', { ascending: false }),
      supabase?.from('credit_notes')?.select('*')?.eq('invoice_id', invoiceId)?.order('issued_at', { ascending: false })
    ]);

    if (invoiceRes?.error) throw invoiceRes?.error;

    const invoice = toCamel(invoiceRes?.data);
    return {
      ...invoice,
      lineItems: toCamel(lineItemsRes?.data || []),
      payments: toCamel(paymentsRes?.data || []),
      creditNotes: toCamel(creditNotesRes?.data || []),
      remainingBalance: Math.max(0, (parseFloat(invoice?.total) || 0) - (parseFloat(invoice?.paidAmount) || 0))
    };
  } catch (err) {
    console.error('getInvoiceById error:', err?.message);
    throw err;
  }
};

export const createInvoice = async (invoiceData, lineItems = []) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const invoiceNumber = await generateInvoiceNumber();
    const year = new Date()?.getFullYear();
    const seqMatch = invoiceNumber?.match(/(\d+)$/);
    const seq = seqMatch ? parseInt(seqMatch?.[1]) : 1;

    // Calculate totals from line items
    const subtotal = lineItems?.reduce((sum, item) => {
      return sum + calculateLineItemAmount(item?.quantity, item?.rate);
    }, 0);
    const { vatAmount, total } = calculateVAT(subtotal, invoiceData?.vatRate || VAT_RATE);

    const payload = {
      id: invoiceNumber,
      lawyer_id: user?.id,
      client_id: invoiceData?.clientId,
      case_id: invoiceData?.caseId || null,
      case_title: invoiceData?.caseTitle || '',
      description: invoiceData?.description || '',
      notes: invoiceData?.notes || '',
      invoice_number: invoiceNumber,
      invoice_year: year,
      invoice_seq: seq,
      issue_date: invoiceData?.issueDate || new Date()?.toISOString()?.split('T')?.[0],
      due_date: invoiceData?.dueDate,
      status: 'draft',
      currency: 'KES',
      subtotal,
      vat_rate: invoiceData?.vatRate || VAT_RATE,
      vat_amount: vatAmount,
      total,
      amount: total,
      paid_amount: 0,
      is_locked: false
    };

    const { data: invoice, error: invError } = await supabase?.from('invoices')?.insert(payload)?.select()?.single();

    if (invError) throw invError;

    // Insert line items
    if (lineItems?.length > 0) {
      const lineItemPayloads = lineItems?.map(item => ({
        invoice_id: invoice?.id,
        time_entry_id: item?.timeEntryId || null,
        description: item?.description,
        item_type: item?.itemType || 'time',
        quantity: parseFloat(item?.quantity) || 1,
        rate: parseFloat(item?.rate) || 0
      }));

      const { error: liError } = await supabase?.from('invoice_line_items')?.insert(lineItemPayloads);

      if (liError) throw liError;
    }

    return toCamel(invoice);
  } catch (err) {
    console.error('createInvoice error:', err?.message);
    throw err;
  }
};

export const updateInvoice = async (invoiceId, updates) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Check if locked
    const { data: existing } = await supabase?.from('invoices')?.select('is_locked, status')?.eq('id', invoiceId)?.eq('lawyer_id', user?.id)?.single();

    if (existing?.is_locked) throw new Error('Invoice is locked and cannot be modified');

    const payload = toSnake(updates);
    delete payload?.id;
    delete payload?.lawyer_id;
    delete payload?.invoice_number;
    delete payload?.created_at;

    const { data, error } = await supabase?.from('invoices')?.update(payload)?.eq('id', invoiceId)?.eq('lawyer_id', user?.id)?.select()?.single();

    if (error) throw error;
    return toCamel(data);
  } catch (err) {
    console.error('updateInvoice error:', err?.message);
    throw err;
  }
};

export const sendInvoice = async (invoiceId) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('invoices')?.update({ status: 'sent', is_locked: true, sent_at: new Date()?.toISOString() })?.eq('id', invoiceId)?.eq('lawyer_id', user?.id)?.eq('status', 'draft')?.select()?.single();

    if (error) throw error;
    return toCamel(data);
  } catch (err) {
    console.error('sendInvoice error:', err?.message);
    throw err;
  }
};

export const deleteInvoice = async (invoiceId) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('invoices')?.delete()?.eq('id', invoiceId)?.eq('lawyer_id', user?.id)?.eq('status', 'draft');

    if (error) throw error;
    return true;
  } catch (err) {
    console.error('deleteInvoice error:', err?.message);
    throw err;
  }
};

// ─── Line Items ───────────────────────────────────────────────────────────────
export const addLineItem = async (invoiceId, itemData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('invoice_line_items')?.insert({
        invoice_id: invoiceId,
        time_entry_id: itemData?.timeEntryId || null,
        description: itemData?.description,
        item_type: itemData?.itemType || 'time',
        quantity: parseFloat(itemData?.quantity) || 1,
        rate: parseFloat(itemData?.rate) || 0
      })?.select()?.single();

    if (error) throw error;
    return toCamel(data);
  } catch (err) {
    console.error('addLineItem error:', err?.message);
    throw err;
  }
};

export const updateLineItem = async (lineItemId, updates) => {
  try {
    const { data, error } = await supabase?.from('invoice_line_items')?.update({
        description: updates?.description,
        item_type: updates?.itemType,
        quantity: parseFloat(updates?.quantity) || 1,
        rate: parseFloat(updates?.rate) || 0
      })?.eq('id', lineItemId)?.select()?.single();

    if (error) throw error;
    return toCamel(data);
  } catch (err) {
    console.error('updateLineItem error:', err?.message);
    throw err;
  }
};

export const deleteLineItem = async (lineItemId) => {
  try {
    const { error } = await supabase?.from('invoice_line_items')?.delete()?.eq('id', lineItemId);

    if (error) throw error;
    return true;
  } catch (err) {
    console.error('deleteLineItem error:', err?.message);
    throw err;
  }
};

// ─── Import Time Entries ──────────────────────────────────────────────────────
export const getBillableTimeEntries = async (caseId) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase?.from('time_entries')?.select('*')?.eq('lawyer_id', user?.id)?.eq('billable_flag', true)?.eq('entry_status', 'draft')?.order('created_at', { ascending: false });

    if (caseId) query = query?.eq('case_id', caseId);

    const { data, error } = await query;
    if (error) throw error;
    return toCamel(data || []);
  } catch (err) {
    console.error('getBillableTimeEntries error:', err?.message);
    throw err;
  }
};

// ─── Payments ─────────────────────────────────────────────────────────────────
export const recordPayment = async (invoiceId, paymentData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('payments')?.insert({
        invoice_id: invoiceId,
        amount: parseFloat(paymentData?.amount),
        payment_method: paymentData?.paymentMethod || 'mpesa',
        payment_date: paymentData?.paymentDate || new Date()?.toISOString()?.split('T')?.[0],
        reference_number: paymentData?.referenceNumber || null,
        mpesa_phone: paymentData?.mpesaPhone || null,
        mpesa_transaction_id: paymentData?.mpesaTransactionId || null,
        notes: paymentData?.notes || null,
        recorded_by: user?.id
      })?.select()?.single();

    if (error) throw error;
    return toCamel(data);
  } catch (err) {
    console.error('recordPayment error:', err?.message);
    throw err;
  }
};

export const getPayments = async (invoiceId) => {
  try {
    const { data, error } = await supabase?.from('payments')?.select('*')?.eq('invoice_id', invoiceId)?.order('payment_date', { ascending: false });

    if (error) throw error;
    return toCamel(data || []);
  } catch (err) {
    console.error('getPayments error:', err?.message);
    throw err;
  }
};

// ─── M-Pesa STK Push (Simulated) ─────────────────────────────────────────────
export const initiateMpesaPayment = async (invoiceId, phoneNumber, amount) => {
  try {
    // In production, this would call the M-Pesa Daraja API via an Edge Function
    // For now, we simulate the STK push initiation
    const transactionId = 'MPE' + Date.now()?.toString()?.slice(-8)?.toUpperCase();
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    return {
      success: true,
      transactionId,
      message: `M-Pesa payment request sent to ${phoneNumber}. Enter PIN to complete.`,
      checkoutRequestId: 'ws_CO_' + Date.now()
    };
  } catch (err) {
    console.error('initiateMpesaPayment error:', err?.message);
    throw err;
  }
};

// ─── Credit Notes ─────────────────────────────────────────────────────────────
export const createCreditNote = async (invoiceId, creditData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const year = new Date()?.getFullYear();
    const creditNoteNumber = `CN-${year}-${String(Math.floor(Math.random() * 9999) + 1)?.padStart(4, '0')}`;

    const { data, error } = await supabase?.from('credit_notes')?.insert({
        invoice_id: invoiceId,
        credit_note_number: creditNoteNumber,
        amount: parseFloat(creditData?.amount),
        reason: creditData?.reason,
        issued_by: user?.id
      })?.select()?.single();

    if (error) throw error;
    return toCamel(data);
  } catch (err) {
    console.error('createCreditNote error:', err?.message);
    throw err;
  }
};

// ─── Clients for invoice creation ────────────────────────────────────────────
export const getClients = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name, email, phone')?.eq('role', 'client')?.order('full_name', { ascending: true });

    if (error) throw error;
    return toCamel(data || []);
  } catch (err) {
    console.error('getClients error:', err?.message);
    return [];
  }
};

// ─── Invoice Analytics ────────────────────────────────────────────────────────
export const getInvoiceAnalytics = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('invoices')?.select('status, total, paid_amount, due_date')?.eq('lawyer_id', user?.id);

    if (error) throw error;

    const invoices = data || [];
    const totalRevenue = invoices?.reduce((s, i) => s + parseFloat(i?.total || 0), 0);
    const totalPaid = invoices?.reduce((s, i) => s + parseFloat(i?.paid_amount || 0), 0);
    const outstanding = invoices?.filter(i => ['sent', 'partially_paid', 'overdue']?.includes(i?.status))?.reduce((s, i) => s + Math.max(0, parseFloat(i?.total || 0) - parseFloat(i?.paid_amount || 0)), 0);
    const overdue = invoices?.filter(i => i?.status === 'overdue' || (i?.status === 'sent' && new Date(i.due_date) < new Date()))?.reduce((s, i) => s + Math.max(0, parseFloat(i?.total || 0) - parseFloat(i?.paid_amount || 0)), 0);

    return {
      totalInvoices: invoices?.length,
      draftCount: invoices?.filter(i => i?.status === 'draft')?.length,
      sentCount: invoices?.filter(i => i?.status === 'sent')?.length,
      paidCount: invoices?.filter(i => i?.status === 'paid')?.length,
      overdueCount: invoices?.filter(i => i?.status === 'overdue')?.length,
      totalRevenue,
      totalPaid,
      outstanding,
      overdue,
      collectionRate: totalRevenue > 0 ? Math.round((totalPaid / totalRevenue) * 100) : 0
    };
  } catch (err) {
    console.error('getInvoiceAnalytics error:', err?.message);
    return { totalInvoices: 0, draftCount: 0, sentCount: 0, paidCount: 0, overdueCount: 0, totalRevenue: 0, totalPaid: 0, outstanding: 0, overdue: 0, collectionRate: 0 };
  }
};

export const formatKES = (amount) => {
  const num = parseFloat(amount) || 0;
  return 'KES ' + num?.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
